import { MAX_FACES, audienceMemberNames, audiencePreviewIds } from './destination-plan';

/**
 * The first five WhatsApp ids of an audience that is NOT loaded, fetched in as
 * few requests as the backend allows — never one per planner row.
 *
 * WHY THIS EXISTS
 * A campaign row's big circle is meant to answer "where is this going" with the
 * faces of the audience. Faces could only be drawn for an audience whose rows
 * another screen had already loaded; every other campaign drew five grey
 * placeholders, which answers nothing. Fetching per row was rejected (the
 * sidebar is virtualised and can hold hundreds of rows), so this loads the
 * previews for EVERY audience that is on screen together:
 *
 *  - ids requested in the same moment (all rows of one paint) are coalesced;
 *  - an audience is fetched once per session and shared by every row and the
 *    hover card that shows it;
 *  - ONE request finds the WhatsApp-id column of all of them, and ONE request
 *    reads the rows of all the small ones — only the id column is selected, and
 *    the row budget is bounded by their known sizes.
 *
 * The backend's find has no "first N per group", so an audience too large to
 * read whole gets its own `$limit: 5` request. Real accounts reuse a handful of
 * small audiences; the common case is exactly two requests for the whole list.
 *
 * WHAT IT DOES NOT DO
 * It never writes into the tasks slice. That slice's loaded-row count is what
 * the "N contacts" figure falls back to (`getTaskSumByBoardIdRecord` prefers a
 * non-zero loaded count), so five preview rows stored there would turn a
 * 40-contact audience into "5 contacts". Previews live here, beside the store.
 */

/** An audience at or under this many rows is read whole, together with the others. */
export const SMALL_AUDIENCE_MAX_ROWS = 25;
/** Most ids a single `$in` carries (the query-string parser's array limit is 20). */
export const MAX_IDS_PER_REQUEST = 20;

export interface PreviewRequest {
    boardIds: string[];
    /** Row cap for the request. */
    limit: number;
}

/**
 * Split the audiences to fetch into row requests. Pure.
 * `knownRows` is each audience's row count where the store knows it
 * (`board.tasksCount`); an unknown size is treated as large.
 */
export function planAudiencePreviewRequests(
    boardIds: readonly string[],
    knownRows: Readonly<Record<string, number | undefined>>,
): PreviewRequest[] {
    const small: string[] = [];
    const large: string[] = [];
    for (const id of new Set(boardIds.filter(Boolean))) {
        const n = knownRows[id];
        if (typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= SMALL_AUDIENCE_MAX_ROWS) small.push(id);
        else large.push(id);
    }
    const requests: PreviewRequest[] = [];
    for (let i = 0; i < small.length; i += MAX_IDS_PER_REQUEST) {
        const chunk = small.slice(i, i + MAX_IDS_PER_REQUEST);
        const rows = chunk.reduce((sum, id) => sum + (knownRows[id] ?? 0), 0);
        // A small margin over the known sizes: a count can lag a just-added row,
        // and a short read costs at most a placeholder face, never a wrong one.
        if (rows > 0) requests.push({ boardIds: chunk, limit: rows + chunk.length * 2 });
    }
    for (const id of large) requests.push({ boardIds: [id], limit: MAX_FACES });
    return requests;
}

export interface PreviewColumn { _id?: string; boardId?: string; type?: string; deleted?: boolean }
export type PreviewRow = Parameters<typeof audiencePreviewIds>[0][number];

export interface AudiencePreviewTransport {
    /** The WhatsApp-id columns of these audiences. */
    findColumns(boardIds: string[]): Promise<PreviewColumn[]>;
    /** Rows of these audiences, projected to `boardId` + the given columns. */
    findRows(boardIds: string[], columnIds: string[], limit: number): Promise<PreviewRow[]>;
}

export interface AudiencePreviewStore {
    /** Ask for these audiences' previews. Cheap to call on every render. */
    request(boardIds: readonly string[], knownRows: Readonly<Record<string, number | undefined>>, transport: AudiencePreviewTransport): void;
    /** Loaded previews (an audience with no WhatsApp ids maps to []). */
    get(boardId: string): readonly string[] | undefined;
    /** A previewed member's name, from the same rows (their `text`). */
    nameOf(contactId: string): string | undefined;
    subscribe(listener: () => void): () => void;
    /** Changes whenever any preview lands — the snapshot for useSyncExternalStore. */
    version(): number;
}

export function createAudiencePreviewStore(options: { schedule?: (fn: () => void) => void } = {}): AudiencePreviewStore {
    const schedule = options.schedule ?? ((fn: () => void) => { setTimeout(fn, 0); });
    const loaded = new Map<string, readonly string[]>();
    const names = new Map<string, string>();
    /** The size an audience had when its preview was read — a change re-reads it. */
    const loadedAtRows = new Map<string, number | undefined>();
    const inFlight = new Set<string>();
    const listeners = new Set<() => void>();
    let pending = new Map<string, number | undefined>();
    let pendingTransport: AudiencePreviewTransport | null = null;
    let scheduled = false;
    let v = 0;

    const emit = () => { v += 1; listeners.forEach(l => l()); };

    const load = async (ids: string[], knownRows: Record<string, number | undefined>, transport: AudiencePreviewTransport) => {
        const settle = (preview: Record<string, string[]>, memberNames: Record<string, string>) => {
            for (const [id, name] of Object.entries(memberNames)) names.set(id, name);
            for (const id of ids) {
                loaded.set(id, preview[id] ?? []);
                loadedAtRows.set(id, knownRows[id]);
                inFlight.delete(id);
            }
            emit();
        };
        try {
            const columns = await transport.findColumns(ids);
            const waColumns = new Map<string, Set<string>>();
            for (const col of columns) {
                if (!col?._id || !col.boardId || col.deleted) continue;
                if (col.type && col.type !== 'whatsapp-id-cell') continue;
                const set = waColumns.get(String(col.boardId)) ?? new Set<string>();
                set.add(String(col._id));
                waColumns.set(String(col.boardId), set);
            }
            const withColumns = ids.filter(id => waColumns.has(id));
            const plan = planAudiencePreviewRequests(withColumns, knownRows);
            const rows = (await Promise.all(plan.map(req => {
                const cols = req.boardIds.flatMap(id => [...(waColumns.get(id) ?? [])]);
                return transport.findRows(req.boardIds, cols, req.limit);
            }))).flat();
            settle(audiencePreviewIds(rows, waColumns), audienceMemberNames(rows, waColumns));
        } catch {
            // A failed read leaves these audiences unloaded (placeholders, as
            // before) and free to be asked for again by a later render.
            for (const id of ids) inFlight.delete(id);
        }
    };

    const flush = () => {
        scheduled = false;
        const transport = pendingTransport;
        const batch = pending;
        pending = new Map();
        pendingTransport = null;
        if (!transport || batch.size === 0) return;
        const ids = [...batch.keys()];
        const known = Object.fromEntries(batch);
        for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
            void load(ids.slice(i, i + MAX_IDS_PER_REQUEST), known, transport);
        }
    };

    return {
        request(boardIds, knownRows, transport) {
            let added = false;
            for (const raw of boardIds) {
                const id = String(raw ?? '');
                if (!id || inFlight.has(id) || pending.has(id)) continue;
                if (loaded.has(id)) {
                    // Re-read only on a REAL change of size. A size that was
                    // unknown when the preview was read (the audience boards had
                    // not loaded yet) and is known now is not a change: the
                    // preview was read with the large-audience cap and is valid.
                    const then = loadedAtRows.get(id);
                    const now = knownRows[id];
                    if (then === undefined || now === undefined || then === now) continue;
                }
                inFlight.add(id);
                pending.set(id, knownRows[id]);
                added = true;
            }
            if (!added) return;
            pendingTransport = transport;
            if (!scheduled) { scheduled = true; schedule(flush); }
        },
        get: id => loaded.get(id),
        nameOf: id => names.get(id),
        subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; },
        version: () => v,
    };
}
